// ============================================================================
// RetainerContracts.jsx
// Monthly retainer contract management for B2B clients
// File: ~/ivision-crm/frontend/src/RetainerContracts.jsx
//
// Wire into RentalDashboard.js:
//   1. import RetainerContracts from './RetainerContracts';
//   2. Add to sidebar nav array:  ['retainers', '📋 Retainers']  (under Operations)
//   3. Add render block:  {view === 'retainers' && <RetainerContracts />}
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from './lib/axiosClient';

const STATUS_STYLES = {
  active:    { bg: '#0d2b1a', color: '#4caf50', border: '#1a4a2a', label: 'Active'    },
  draft:     { bg: '#1a1a2b', color: '#7080f0', border: '#2a2a4a', label: 'Draft'     },
  expired:   { bg: '#2b0d0d', color: '#f04040', border: '#4a1a1a', label: 'Expired'   },
  cancelled: { bg: '#1a1a1a', color: '#666',    border: '#333',    label: 'Cancelled' },
};

function Badge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color }} />
      {s.label}
    </span>
  );
}

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysLeft(end_date) {
  if (!end_date) return null;
  return Math.ceil((new Date(end_date) - new Date()) / 86400000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main module
// ─────────────────────────────────────────────────────────────────────────────
export default function RetainerContracts() {
  const [view, setView]           = useState('list');   // 'list' | 'detail' | 'new'
  const [contracts, setContracts] = useState([]);
  const [metrics, setMetrics]     = useState(null);
  const [selected, setSelected]   = useState(null);
  const [filterStatus, setFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      const [cRes, mRes] = await Promise.all([
        axiosClient.get(`/api/rentals/retainers?${params}`),
        axiosClient.get('/api/rentals/retainers/metrics'),
      ]);
      setContracts(cRes.data.contracts || []);
      setMetrics(mRes.data.metrics || null);
    } catch (err) {
      console.error('load retainers error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const filtered = contracts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.title?.toLowerCase().includes(q) ||
      c.contract_number?.toLowerCase().includes(q) ||
      c.rental_clients?.name?.toLowerCase().includes(q) ||
      c.rental_clients?.company?.toLowerCase().includes(q)
    );
  });

  const openDetail = async (id) => {
    try {
      const res = await axiosClient.get(`/api/rentals/retainers/${id}`);
      setSelected(res.data.contract);
      setView('detail');
    } catch (err) {
      console.error('open detail error:', err);
    }
  };

  if (view === 'new')    return <RetainerForm onSaved={() => { load(); setView('list'); }} onCancel={() => setView('list')} />;
  if (view === 'detail' && selected) return (
    <RetainerDetail
      contract={selected}
      onBack={() => { setSelected(null); setView('list'); }}
      onRefresh={async () => {
        const res = await axiosClient.get(`/api/rentals/retainers/${selected.id}`);
        setSelected(res.data.contract);
      }}
      onDelete={() => { load(); setView('list'); setSelected(null); }}
    />
  );

  // ── List view ──
  const S = {
    wrap:      { padding: '0 0 40px' },
    header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
    title:     { fontSize: 22, fontWeight: 600, color: '#f9fafb' },
    btn:       { padding: '9px 18px', background: '#7c6fcd', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    metrics:   { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 },
    metCard:   { background: '#111', border: '1px solid #222', borderRadius: 8, padding: '14px 18px' },
    metNum:    { fontSize: 24, fontWeight: 600, color: '#f9fafb', lineHeight: 1, marginBottom: 4 },
    metLabel:  { fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.08em' },
    filters:   { display: 'flex', gap: 10, marginBottom: 16 },
    input:     { padding: '8px 12px', background: '#111', border: '1px solid #222', borderRadius: 6, color: '#e5e7eb', fontSize: 13, flex: 1, outline: 'none' },
    select:    { padding: '8px 12px', background: '#111', border: '1px solid #222', borderRadius: 6, color: '#e5e7eb', fontSize: 13, outline: 'none' },
    table:     { width: '100%', borderCollapse: 'collapse' },
    th:        { padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #222' },
    td:        { padding: '13px 14px', fontSize: 13, color: '#d1d5db', borderBottom: '1px solid #1a1a1a', verticalAlign: 'middle' },
    empty:     { textAlign: 'center', padding: '48px 0', color: '#555', fontSize: 14 },
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>📋 Retainer Contracts</div>
        <button style={S.btn} onClick={() => setView('new')}>+ New Retainer</button>
      </div>

      {/* Metrics */}
      {metrics && (
        <div style={S.metrics}>
          <div style={S.metCard}>
            <div style={{ ...S.metNum, color: '#4caf50' }}>{metrics.active}</div>
            <div style={S.metLabel}>Active</div>
          </div>
          <div style={S.metCard}>
            <div style={{ ...S.metNum, color: '#7c6fcd' }}>{fmt(metrics.monthly_revenue)}</div>
            <div style={S.metLabel}>Monthly Revenue</div>
          </div>
          <div style={S.metCard}>
            <div style={{ ...S.metNum, color: '#f0c040' }}>{metrics.expiring_soon}</div>
            <div style={S.metLabel}>Expiring in 30 days</div>
          </div>
          <div style={S.metCard}>
            <div style={{ ...S.metNum, color: '#9ca3af' }}>{metrics.draft}</div>
            <div style={S.metLabel}>Draft</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={S.filters}>
        <input
          style={S.input}
          placeholder="Search client, title, contract #..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={S.select} value={filterStatus} onChange={e => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={S.empty}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={S.empty}>
          {contracts.length === 0
            ? 'No retainer contracts yet. Create your first one to start locking in recurring revenue.'
            : 'No contracts match your search.'}
        </div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>#</th>
              <th style={S.th}>Client</th>
              <th style={S.th}>Title</th>
              <th style={S.th}>Monthly Rate</th>
              <th style={S.th}>Equipment</th>
              <th style={S.th}>Term</th>
              <th style={S.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const days = daysLeft(c.end_date);
              return (
                <tr
                  key={c.id}
                  onClick={() => openDetail(c.id)}
                  style={{ cursor: 'pointer' }}
                  onMouseOver={e  => e.currentTarget.style.background = '#0f0f0f'}
                  onMouseOut={e   => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ ...S.td, color: '#555', fontFamily: 'monospace', fontSize: 12 }}>{c.contract_number}</td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 500, color: '#f9fafb' }}>{c.rental_clients?.name || '—'}</div>
                    {c.rental_clients?.company && <div style={{ fontSize: 11, color: '#666' }}>{c.rental_clients.company}</div>}
                  </td>
                  <td style={S.td}>{c.title}</td>
                  <td style={{ ...S.td, fontWeight: 600, color: '#4caf50' }}>{fmt(c.monthly_rate)}/mo</td>
                  <td style={{ ...S.td, color: '#9ca3af' }}>{c.item_count} item{c.item_count !== 1 ? 's' : ''}</td>
                  <td style={S.td}>
                    <div style={{ fontSize: 12 }}>{fmtDate(c.start_date)} – {fmtDate(c.end_date)}</div>
                    {days !== null && c.status === 'active' && (
                      <div style={{ fontSize: 11, color: days < 0 ? '#f04040' : days <= 30 ? '#f0c040' : '#555', marginTop: 2 }}>
                        {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Expires today' : `${days}d left`}
                      </div>
                    )}
                  </td>
                  <td style={S.td}><Badge status={c.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RetainerDetail — full contract view with equipment list
// ─────────────────────────────────────────────────────────────────────────────
function RetainerDetail({ contract: c, onBack, onRefresh, onDelete }) {
  const [editing, setEditing]         = useState(false);
  const [addingItem, setAddingItem]   = useState(false);
  const [fleetItems, setFleetItems]   = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [itemNotes, setItemNotes]     = useState('');
  const [itemQty, setItemQty]         = useState(1);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [toast, setToast]             = useState('');
  const [usageOrders, setUsageOrders] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [showUsage, setShowUsage]     = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [showOrderPreview, setShowOrderPreview] = useState(false);
  const [orderPreviewDates, setOrderPreviewDates] = useState({ from: new Date().toISOString().slice(0,10), to: new Date(Date.now()+86400000).toISOString().slice(0,10) });
  const [showRenew, setShowRenew]     = useState(false);
  const [renewMonths, setRenewMonths] = useState(1);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const [form, setForm] = useState({
    title: c.title, status: c.status, monthly_rate: c.monthly_rate,
    billing_day: c.billing_day || 1, start_date: c.start_date || '',
    end_date: c.end_date || '', notes: c.notes || '',
  });

  useEffect(() => {
    axiosClient.get('/api/rentals/items?limit=200').then(res => {
      setFleetItems(res.data.items || []);
    }).catch(() => {});
    loadUsage();
  }, []);

  const loadUsage = async () => {
    setUsageLoading(true);
    try {
      const r = await axiosClient.get(`/api/rentals/retainers/${c.id}/orders`);
      setUsageOrders(r.data.orders || []);
    } catch { setUsageOrders([]); }
    setUsageLoading(false);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await axiosClient.patch(`/api/rentals/retainers/${c.id}`, {
        ...form, monthly_rate: parseFloat(form.monthly_rate),
        billing_day: parseInt(form.billing_day),
        start_date: form.start_date || null, end_date: form.end_date || null,
      });
      await onRefresh(); setEditing(false); showToast('Contract updated');
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete retainer "${c.title}"?`)) return;
    try { await axiosClient.delete(`/api/rentals/retainers/${c.id}`); onDelete(); }
    catch { setError('Could not delete retainer'); }
  };

  const handleAddItem = async () => {
    if (!selectedItemId) return;
    setSaving(true); setError('');
    try {
      await axiosClient.post(`/api/rentals/retainers/${c.id}/items`, {
        product_group_id: selectedItemId, notes: itemNotes || null, quantity: itemQty,
      });
      setAddingItem(false); setSelectedItemId(''); setItemNotes(''); setItemQty(1);
      await onRefresh(); showToast('Equipment added');
    } catch (err) { setError(err.response?.data?.error || 'Could not add item'); }
    finally { setSaving(false); }
  };

  const handleRemoveItem = async (id) => {
    if (!window.confirm('Remove this equipment?')) return;
    try { await axiosClient.delete(`/api/rentals/retainers/${c.id}/items/${id}`); await onRefresh(); showToast('Removed'); }
    catch { setError('Could not remove item'); }
  };

  const handleRenew = async () => {
    const currentEnd = c.end_date ? new Date(c.end_date) : new Date();
    const newEnd = new Date(currentEnd);
    newEnd.setMonth(newEnd.getMonth() + renewMonths);
    try {
      await axiosClient.patch(`/api/rentals/retainers/${c.id}`, {
        end_date: newEnd.toISOString().slice(0,10), status: 'active',
      });
      await onRefresh(); setShowRenew(false); showToast(`Renewed for ${renewMonths} month(s)`);
    } catch { setError('Could not renew'); }
  };

  const handleCreateOrder = async () => {
    setCreatingOrder(true);
    try {
      const orderItems = items.map(ri => ({
        rental_item_id: ri.rental_product_groups?.id || ri.product_group_id,
        item_name: ri.rental_product_groups?.name || 'Equipment',
        quantity: ri.quantity || 1,
        unit_price: ri.rental_product_groups?.base_price_day || 0,
        total_price: ri.rental_product_groups?.base_price_day || 0,
        rate_type: 'day',
      }));
      const r = await axiosClient.post('/api/rentals/orders', {
        client_id: c.rental_clients.id, client_name: c.rental_clients.name,
        client_email: c.rental_clients.email || '', client_phone: c.rental_clients.phone || '',
        client_company: c.rental_clients.company || '',
        status: 'draft', payment_status: 'payment_due', fulfillment_type: 'pickup',
        from_date: orderPreviewDates.from, to_date: orderPreviewDates.to,
        notes: `From retainer: ${c.title} (${c.contract_number})`,
        retainer_id: c.id, items: orderItems,
      });
      if (r.data.order?.id) {
        showToast(`Order ${r.data.order.order_number||'#'+r.data.order.id} created!`);
        setShowOrderPreview(false); await loadUsage();
      }
    } catch (err) { setError(err.response?.data?.error || 'Could not create order'); }
    finally { setCreatingOrder(false); }
  };

  const items = c.rental_retainer_items || [];
  const client = c.rental_clients || {};
  const days = daysLeft(c.end_date);

  // Financial calculations
  const equipRetailValue = items.reduce((s, ri) => s + Number(ri.rental_product_groups?.base_price_day || 0) * 22 * (ri.quantity||1), 0);
  const savings = equipRetailValue - Number(c.monthly_rate || 0);
  const thisMonthOrders = usageOrders.filter(o => {
    const d = new Date(o.created_at || o.from_date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const thisMonthSpend = thisMonthOrders.reduce((s,o) => s + Number(o.total||0), 0);

  const S = {
    card:   { background: '#111', border: '1px solid #222', borderRadius: 10, padding: '20px 24px', marginBottom: 16 },
    label:  { fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
    value:  { fontSize: 14, color: '#e5e7eb' },
    input:  { width: '100%', padding: '9px 12px', background: '#0a0a0a', border: '1px solid #333', borderRadius: 6, color: '#e5e7eb', fontSize: 13, outline: 'none' },
    row:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
    btnP:   { padding: '9px 20px', background: '#7c6fcd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnG:   { padding: '9px 20px', background: 'transparent', color: '#9ca3af', border: '1px solid #333', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
    btnD:   { padding: '9px 16px', background: 'transparent', color: '#f04040', border: '1px solid #4a1a1a', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
    btnSm:  { padding: '6px 12px', background: 'transparent', color: '#7c6fcd', border: '1px solid #2a2a4a', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
    btnSmD: { padding: '6px 12px', background: 'transparent', color: '#f04040', border: '1px solid #4a1a1a', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Back + actions */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:8 }}>
        <button style={{ background:'none', border:'none', color:'#9ca3af', fontSize:13, cursor:'pointer', padding:0 }} onClick={onBack}>← Back to Retainers</button>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button style={{...S.btnP, background:'#14532d', color:'#4ade80'}} onClick={()=>setShowOrderPreview(true)}>+ Create Order</button>
          <button style={{...S.btnG, color:'#60a5fa', borderColor:'#1a2a4a'}} onClick={()=>setShowRenew(v=>!v)}>↻ Renew</button>
          {!editing && <button style={S.btnG} onClick={()=>setEditing(true)}>Edit</button>}
          <button style={S.btnD} onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {toast && <div style={{background:'#0d2b1a',border:'1px solid #1a4a2a',borderRadius:8,padding:'10px 16px',marginBottom:16,color:'#4caf50',fontSize:13}}>✓ {toast}</div>}
      {error && <div style={{background:'#2b0d0d',border:'1px solid #4a1a1a',borderRadius:8,padding:'10px 16px',marginBottom:16,color:'#f04040',fontSize:13}}>{error}</div>}

      {/* Renewal alerts */}
      {days !== null && days <= 30 && days >= 0 && c.status === 'active' && (
        <div style={{background:'#2a1f00',border:'1px solid #4a3800',borderRadius:8,padding:'10px 16px',marginBottom:16,color:'#fbbf24',fontSize:13}}>
          ⚠️ Expires in <strong>{days} day{days!==1?'s':''}</strong> — renew before {fmtDate(c.end_date)}
        </div>
      )}
      {days !== null && days < 0 && c.status === 'active' && (
        <div style={{background:'#2b0d0d',border:'1px solid #4a1a1a',borderRadius:8,padding:'10px 16px',marginBottom:16,color:'#f87171',fontSize:13}}>
          🚨 Expired {Math.abs(days)} day{Math.abs(days)!==1?'s':''} ago
        </div>
      )}

      {/* Renew panel */}
      {showRenew && (
        <div style={{...S.card, background:'#0d1a2b', border:'1px solid #1a3050', marginBottom:16}}>
          <div style={{fontWeight:700,color:'#60a5fa',marginBottom:12,fontSize:13}}>↻ Renew Retainer</div>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:12,color:'#9ca3af'}}>Extend by:</span>
            {[1,3,6,12].map(m=>(
              <button key={m} onClick={()=>setRenewMonths(m)}
                style={{...S.btnSm, background:renewMonths===m?'#1a3a6a':'transparent', color:renewMonths===m?'#60a5fa':'#9ca3af'}}>
                {m} mo
              </button>
            ))}
            <button style={{...S.btnP, background:'#1a3a6a', color:'#60a5fa'}} onClick={handleRenew}>
              Renew → {c.end_date ? fmtDate(new Date(new Date(c.end_date).setMonth(new Date(c.end_date).getMonth()+renewMonths)).toISOString().slice(0,10)) : 'Set end date first'}
            </button>
            <button style={S.btnG} onClick={()=>setShowRenew(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Order preview modal */}
      {showOrderPreview && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #333',borderRadius:12,width:'100%',maxWidth:500,padding:24}}>
            <div style={{fontSize:16,fontWeight:700,color:'#f9fafb',marginBottom:4}}>Create Order from Retainer</div>
            <div style={{fontSize:12,color:'#555',marginBottom:16}}>{c.title} · {client.name}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
              <div>
                <div style={S.label}>From Date</div>
                <input type="date" style={S.input} value={orderPreviewDates.from} onChange={e=>setOrderPreviewDates(p=>({...p,from:e.target.value}))} />
              </div>
              <div>
                <div style={S.label}>To Date</div>
                <input type="date" style={S.input} value={orderPreviewDates.to} onChange={e=>setOrderPreviewDates(p=>({...p,to:e.target.value}))} />
              </div>
            </div>
            <div style={{background:'#0a0a0a',border:'1px solid #222',borderRadius:8,padding:12,marginBottom:16}}>
              <div style={{fontSize:11,color:'#555',fontWeight:700,marginBottom:8}}>EQUIPMENT ({items.length} items)</div>
              {items.map(ri=>(
                <div key={ri.id} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid #111',fontSize:12}}>
                  <span style={{color:'#e5e7eb'}}>{ri.rental_product_groups?.name||'Equipment'}</span>
                  <span style={{color:'#9ca3af'}}>x{ri.quantity||1} · ${ri.rental_product_groups?.base_price_day||0}/day</span>
                </div>
              ))}
              {items.length === 0 && <div style={{color:'#555',fontSize:12}}>No equipment on this retainer yet</div>}
            </div>
            <div style={{display:'flex',gap:10}}>
              <button style={{...S.btnP,background:'#14532d',color:'#4ade80',flex:1}} onClick={handleCreateOrder} disabled={creatingOrder}>
                {creatingOrder ? 'Creating...' : `Create Order (${items.length} items)`}
              </button>
              <button style={S.btnG} onClick={()=>setShowOrderPreview(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header card */}
      <div style={{...S.card, display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:16}}>
        <div>
          <div style={{fontSize:11,color:'#555',fontFamily:'monospace',marginBottom:4}}>{c.contract_number}</div>
          <div style={{fontSize:20,fontWeight:600,color:'#f9fafb',marginBottom:8}}>{c.title}</div>
          <Badge status={c.status} />
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:28,fontWeight:600,color:'#4caf50',lineHeight:1}}>{fmt(c.monthly_rate)}</div>
          <div style={{fontSize:12,color:'#666',marginTop:3}}>per month · due day {c.billing_day}</div>
          <div style={{fontSize:12,color:'#555',marginTop:3}}>{fmt(Number(c.monthly_rate||0)*12)}/year</div>
          {days !== null && c.status === 'active' && (
            <div style={{fontSize:12,color:days<0?'#f04040':days<=30?'#f0c040':'#555',marginTop:6}}>
              {days < 0 ? `Expired ${Math.abs(days)}d ago` : days===0 ? 'Expires today' : `${days} days left`}
            </div>
          )}
        </div>
      </div>

      {/* Financial summary */}
      <div style={{...S.card}}>
        <div style={{...S.label, marginBottom:14}}>Financial Breakdown</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
          <div style={{background:'#0a0a0a',borderRadius:8,padding:'12px 14px',border:'1px solid #1a1a1a'}}>
            <div style={{fontSize:11,color:'#555',marginBottom:4}}>Monthly Rate</div>
            <div style={{fontSize:18,fontWeight:700,color:'#4caf50'}}>{fmt(c.monthly_rate)}</div>
          </div>
          <div style={{background:'#0a0a0a',borderRadius:8,padding:'12px 14px',border:'1px solid #1a1a1a'}}>
            <div style={{fontSize:11,color:'#555',marginBottom:4}}>Retail Value (22 days)</div>
            <div style={{fontSize:18,fontWeight:700,color:'#f97316'}}>{fmt(equipRetailValue)}</div>
            {savings > 0 && <div style={{fontSize:10,color:'#4ade80',marginTop:2}}>Client saves {fmt(savings)}/mo</div>}
          </div>
          <div style={{background:'#0a0a0a',borderRadius:8,padding:'12px 14px',border:'1px solid #1a1a1a'}}>
            <div style={{fontSize:11,color:'#555',marginBottom:4}}>This Month Orders</div>
            <div style={{fontSize:18,fontWeight:700,color:'#60a5fa'}}>{thisMonthOrders.length}</div>
            <div style={{fontSize:10,color:'#555',marginTop:2}}>{fmt(thisMonthSpend)} billed</div>
          </div>
        </div>
      </div>

      {/* Client info */}
      <div style={S.card}>
        <div style={{...S.label, marginBottom:12}}>Client</div>
        <div style={{fontSize:15,fontWeight:500,color:'#f9fafb'}}>{client.name}</div>
        {client.company && <div style={{fontSize:13,color:'#9ca3af',marginTop:2}}>{client.company}</div>}
        <div style={{display:'flex',gap:20,marginTop:10}}>
          {client.email && <span style={{fontSize:12,color:'#7c6fcd'}}>{client.email}</span>}
          {client.phone && <span style={{fontSize:12,color:'#9ca3af'}}>{client.phone}</span>}
        </div>
      </div>

      {/* Edit / view fields */}
      {editing ? (
        <div style={S.card}>
          <div style={S.row}>
            <div><div style={S.label}>Title</div><input style={S.input} value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} /></div>
            <div><div style={S.label}>Status</div>
              <select style={S.input} value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                <option value="draft">Draft</option><option value="active">Active</option>
                <option value="expired">Expired</option><option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div style={S.row}>
            <div><div style={S.label}>Monthly Rate ($)</div><input type="number" style={S.input} value={form.monthly_rate} onChange={e=>setForm(p=>({...p,monthly_rate:e.target.value}))} /></div>
            <div><div style={S.label}>Billing Day</div><input type="number" style={S.input} value={form.billing_day} onChange={e=>setForm(p=>({...p,billing_day:e.target.value}))} min="1" max="28" /></div>
          </div>
          <div style={S.row}>
            <div><div style={S.label}>Start Date</div><input type="date" style={S.input} value={form.start_date} onChange={e=>setForm(p=>({...p,start_date:e.target.value}))} /></div>
            <div><div style={S.label}>End Date</div><input type="date" style={S.input} value={form.end_date} onChange={e=>setForm(p=>({...p,end_date:e.target.value}))} /></div>
          </div>
          <div style={{marginBottom:20}}>
            <div style={S.label}>Notes</div>
            <textarea style={{...S.input,height:80,resize:'vertical'}} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} />
          </div>
          <div style={{display:'flex',gap:10}}>
            <button style={S.btnP} onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save'}</button>
            <button style={S.btnG} onClick={()=>{setEditing(false);setError('');}}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={S.card}>
          <div style={S.row}>
            <div><div style={S.label}>Start Date</div><div style={S.value}>{fmtDate(c.start_date)}</div></div>
            <div><div style={S.label}>End Date</div><div style={S.value}>{fmtDate(c.end_date)}</div></div>
          </div>
          <div style={S.row}>
            <div><div style={S.label}>Billing Day</div><div style={S.value}>Day {c.billing_day} of month</div></div>
            <div><div style={S.label}>Contract #</div><div style={{...S.value,fontFamily:'monospace',fontSize:12}}>{c.contract_number}</div></div>
          </div>
          {c.notes && <div><div style={S.label}>Notes</div><div style={{...S.value,whiteSpace:'pre-wrap',lineHeight:1.6}}>{c.notes}</div></div>}
        </div>
      )}

      {/* Equipment */}
      <div style={S.card}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <div style={{...S.label,marginBottom:0}}>Equipment on Retainer ({items.length})</div>
          <button style={S.btnSm} onClick={()=>setAddingItem(v=>!v)}>{addingItem?'Cancel':'+ Add Equipment'}</button>
        </div>
        {addingItem && (
          <div style={{background:'#0a0a0a',border:'1px solid #222',borderRadius:8,padding:'14px 16px',marginBottom:16}}>
            <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr auto',gap:10,alignItems:'flex-end'}}>
              <div>
                <div style={S.label}>Equipment</div>
                <select style={S.input} value={selectedItemId} onChange={e=>setSelectedItemId(e.target.value)}>
                  <option value="">— choose —</option>
                  {fleetItems.filter(fi=>!items.some(ri=>ri.rental_product_groups?.id===fi.id)).map(fi=>(
                    <option key={fi.id} value={fi.id}>{fi.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={S.label}>Qty</div>
                <input type="number" min="1" style={S.input} value={itemQty} onChange={e=>setItemQty(Number(e.target.value))} />
              </div>
              <div>
                <div style={S.label}>Notes</div>
                <input style={S.input} value={itemNotes} onChange={e=>setItemNotes(e.target.value)} placeholder="optional" />
              </div>
              <button style={{...S.btnP,whiteSpace:'nowrap'}} onClick={handleAddItem} disabled={saving||!selectedItemId}>Add</button>
            </div>
          </div>
        )}
        {items.length === 0 ? (
          <div style={{color:'#555',fontSize:13,padding:'12px 0'}}>No equipment added yet.</div>
        ) : (
          <div>
            {/* Equipment value summary */}
            <div style={{background:'#0a0a0a',borderRadius:8,padding:'10px 14px',marginBottom:12,border:'1px solid #1a1a1a',display:'flex',gap:24,flexWrap:'wrap'}}>
              <div><span style={{fontSize:11,color:'#555'}}>Items: </span><span style={{fontSize:12,color:'#e5e7eb',fontWeight:600}}>{items.length}</span></div>
              <div><span style={{fontSize:11,color:'#555'}}>Daily retail value: </span><span style={{fontSize:12,color:'#f97316',fontWeight:600}}>{fmt(items.reduce((s,ri)=>s+Number(ri.rental_product_groups?.base_price_day||0)*(ri.quantity||1),0))}/day</span></div>
              <div><span style={{fontSize:11,color:'#555'}}>Monthly retail (22 days): </span><span style={{fontSize:12,color:'#f97316',fontWeight:600}}>{fmt(equipRetailValue)}</span></div>
              {savings > 0 && <div><span style={{fontSize:11,color:'#555'}}>Client saves: </span><span style={{fontSize:12,color:'#4ade80',fontWeight:600}}>{fmt(savings)}/mo</span></div>}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {items.map(ri => {
                const item = ri.rental_product_groups || {};
                const dayRate = Number(item.base_price_day||0);
                return (
                  <div key={ri.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#0a0a0a',border:'1px solid #1a1a1a',borderRadius:8}}>
                    <div style={{display:'flex',alignItems:'center',gap:12,flex:1}}>
                      <div style={{width:36,height:36,borderRadius:6,background:'#222',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>🔧</div>
                      <div>
                        <div style={{fontSize:13,fontWeight:500,color:'#f9fafb'}}>{item.name||'Unknown'}</div>
                        <div style={{display:'flex',gap:12,marginTop:2}}>
                          <span style={{fontSize:11,color:'#555'}}>Qty: <span style={{color:'#e5e7eb'}}>{ri.quantity||1}</span></span>
                          {dayRate > 0 && <span style={{fontSize:11,color:'#555'}}>Day rate: <span style={{color:'#f97316'}}>{fmt(dayRate)}</span></span>}
                          {dayRate > 0 && <span style={{fontSize:11,color:'#555'}}>Monthly retail: <span style={{color:'#f97316'}}>{fmt(dayRate*22*(ri.quantity||1))}</span></span>}
                        </div>
                        {ri.notes && <div style={{fontSize:11,color:'#9ca3af',marginTop:2,fontStyle:'italic'}}>{ri.notes}</div>}
                      </div>
                    </div>
                    <button style={S.btnSmD} onClick={()=>handleRemoveItem(ri.id)}>Remove</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Usage History */}
      <div style={S.card}>
        <div style={{fontWeight:700,color:'#e5e7eb',marginBottom:12,fontSize:13}}>📋 Order History ({usageOrders.length})</div>
        {usageLoading && <div style={{color:'#555',padding:12}}>Loading...</div>}
        {!usageLoading && usageOrders.length === 0 && <div style={{color:'#555',fontSize:13}}>No orders yet — hit + Create Order to start.</div>}
        {usageOrders.map(o=>(
          <div key={o.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #1a1a1a',flexWrap:'wrap',gap:8}}>
            <span style={{fontSize:12,fontWeight:700,color:'#60a5fa',fontFamily:'monospace'}}>{o.order_number||'#'+o.id}</span>
            <span style={{fontSize:11,color:'#9ca3af'}}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
            <span style={{fontSize:12,fontWeight:700,color:'#f97316'}}>{fmt(o.total)}</span>
            <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'#252840',color:'#9ca3af',textTransform:'capitalize'}}>{o.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RetainerForm — create new retainer
// ─────────────────────────────────────────────────────────────────────────────
function RetainerForm({ onSaved, onCancel }) {
  const [clients, setClients]     = useState([]);
  const [fleetItems, setFleet]    = useState([]);
  const [selectedEquip, setSelectedEquip] = useState([]);
  const [equipSearch, setEquipSearch] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [form, setForm]           = useState({
    client_id:    '',
    title:        '',
    status:       'draft',
    monthly_rate: '',
    billing_day:  1,
    start_date:   '',
    end_date:     '',
    notes:        '',
  });

  const [clientSearch, setClientSearch] = useState('');
  useEffect(() => {
    axiosClient.get('/api/rentals/clients?limit=500').then(res => {
      setClients(res.data.clients || res.data || []);
    }).catch(() => {});
    axiosClient.get('/api/rentals/items?limit=200').then(res => {
      setFleet(res.data.items || []);
    }).catch(() => {});
  }, []);

  const toggleEquip = (id) => setSelectedEquip(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleSubmit = async () => {
    if (!form.client_id)    return setError('Select a client');
    if (!form.title.trim()) return setError('Enter a contract title');
    if (!form.monthly_rate) return setError('Enter a monthly rate');

    setSaving(true); setError('');
    try {
      const res = await axiosClient.post('/api/rentals/retainers', {
        ...form,
        monthly_rate: parseFloat(form.monthly_rate),
        billing_day:  parseInt(form.billing_day),
        start_date:   form.start_date || null,
        end_date:     form.end_date   || null,
      });
      const retainerId = res.data.contract?.id;
      if (retainerId && selectedEquip.length > 0) {
        await Promise.all(selectedEquip.map(id =>
          axiosClient.post(`/api/rentals/retainers/${retainerId}/items`, { product_group_id: id })
        ));
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create retainer');
    } finally {
      setSaving(false);
    }
  };

  const S = {
    wrap:   { maxWidth: 680 },
    card:   { background: '#111', border: '1px solid #222', borderRadius: 10, padding: '24px', marginBottom: 16 },
    label:  { fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
    input:  { width: '100%', padding: '9px 12px', background: '#0a0a0a', border: '1px solid #333', borderRadius: 6, color: '#e5e7eb', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
    row:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
    btnP:   { padding: '10px 24px', background: '#7c6fcd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnG:   { padding: '10px 24px', background: 'transparent', color: '#9ca3af', border: '1px solid #333', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  };

  return (
    <div style={S.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#f9fafb' }}>New Retainer Contract</div>
        <button style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer' }} onClick={onCancel}>← Cancel</button>
      </div>

      {error && <div style={{ background: '#2b0d0d', border: '1px solid #4a1a1a', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#f04040', fontSize: 13 }}>{error}</div>}

      <div style={S.card}>
        <div style={{ marginBottom: 16 }}>
          <div style={S.label}>Client *</div>
          <select style={S.input} value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}>
            <option value="">— select client —</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={S.label}>Contract Title *</div>
          <input style={S.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. ABC Concrete — 2026 Fleet Agreement" />
        </div>

        <div style={S.row}>
          <div>
            <div style={S.label}>Monthly Rate ($) *</div>
            <input type="number" style={S.input} value={form.monthly_rate} onChange={e => setForm(p => ({ ...p, monthly_rate: e.target.value }))} placeholder="2000" min="0" step="50" />
          </div>
          <div>
            <div style={S.label}>Billing Day of Month</div>
            <input type="number" style={S.input} value={form.billing_day} onChange={e => setForm(p => ({ ...p, billing_day: e.target.value }))} min="1" max="28" />
          </div>
        </div>

        <div style={S.row}>
          <div>
            <div style={S.label}>Start Date</div>
            <input type="date" style={S.input} value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
          </div>
          <div>
            <div style={S.label}>End Date</div>
            <input type="date" style={S.input} value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={S.label}>Status</div>
          <select style={S.input} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
          </select>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={S.label}>Notes</div>
          <textarea style={{ ...S.input, height: 80, resize: 'vertical' }} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Contract terms, included equipment, special conditions..." />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.btnP} onClick={handleSubmit} disabled={saving}>{saving ? 'Creating...' : 'Create Retainer'}</button>
          <button style={S.btnG} onClick={onCancel}>Cancel</button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Equipment Included ({selectedEquip.length} selected)</div>
        <input value={equipSearch} onChange={e=>setEquipSearch(e.target.value)} placeholder="Search equipment..." style={{ width:'100%', padding:'9px 12px', background:'#0a0a0a', border:'1px solid #333', borderRadius:6, color:'#e5e7eb', fontSize:13, outline:'none', boxSizing:'border-box', marginBottom:8 }} />
        <div style={{maxHeight:200,overflowY:'auto',border:'1px solid #333',borderRadius:6,background:'#0a0a0a'}}>
          {fleetItems.filter(i => !equipSearch || i.name.toLowerCase().includes(equipSearch.toLowerCase())).map(i => (
            <div key={i.id} onClick={()=>setSelectedEquip(prev=>prev.includes(i.id)?prev.filter(x=>x!==i.id):[...prev,i.id])}
              style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',cursor:'pointer',background:selectedEquip.includes(i.id)?'rgba(124,111,205,0.15)':'transparent',borderBottom:'1px solid #111'}}>
              <div style={{width:16,height:16,borderRadius:3,border:`2px solid ${selectedEquip.includes(i.id)?'#7c6fcd':'#333'}`,background:selectedEquip.includes(i.id)?'#7c6fcd':'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                {selectedEquip.includes(i.id) && <span style={{color:'#fff',fontSize:10,fontWeight:700}}>✓</span>}
              </div>
              <span style={{fontSize:12,color:selectedEquip.includes(i.id)?'#c4b5fd':'#e5e7eb'}}>{i.name}</span>
              {i.category && <span style={{fontSize:10,color:'#555',marginLeft:'auto'}}>{i.category}</span>}
            </div>
          ))}
          {fleetItems.length === 0 && <div style={{padding:12,color:'#555',fontSize:12}}>Loading equipment...</div>}
        </div>
      </div>
    </div>
  );
}
